// AEO-08a: one sentence of real venue data for every venue photo.
//
// WHY THIS EXISTS. A multimodal model cannot see a photo. It reads the alt
// text. Venue photos are this site's core content object, so a photo with no
// alt is a venue that an answer engine -- and a screen reader -- cannot read
// at all. This builds the sentence once, so /search/ and the venue page say
// the same thing about the same venue.
//
// WHY EVERY FIELD IS OPTIONAL. Venue records are written by hosts in
// Firestore. Any field can be missing, and `address` itself can be missing.
// A template that reads venue.address.city throws on such a record, and a
// template that interpolates a missing field publishes the word "undefined"
// to an answer engine, which is worse than saying nothing. So each part is
// dropped when it is absent. A record with only a name is valid. A record
// with nothing at all is valid too -- it gets the bare fallback.
//
// Output, from a full record:
//   "Sunset Barn, an outdoor barn event venue in Heber City, UT"
// From a name-only record:
//   "Sunset Barn, an event venue"
// From an empty record:
//   "An event venue"

const clean = (value) => (typeof value === 'string' ? value.trim() : '');

// "a" or "an", decided by the first letter of the descriptor.
//
// `u` is deliberately NOT a vowel here. The only descriptor in the data that
// starts with one is "unique space", which takes "a unique space" because the
// u is pronounced "yoo". Every other vowel start comes from the setting
// field -- "an outdoor", "an indoor" -- and takes "an". The real vocabulary
// is the two lists in src/components/helpers.js: venueSettings and venueTypes.
const article = (phrase) => (/^[aeio]/i.test(phrase) ? 'an' : 'a');

const capitalise = (text) => (text ? `${text[0].toUpperCase()}${text.slice(1)}` : text);

// `index` is zero-based. The position is added only when a venue has more
// than one photo, so a gallery does not repeat one identical sentence three
// times over three different pictures.
const photoPosition = (index, total) => (Number.isFinite(index) && Number.isFinite(total) && total > 1 ? `photo ${index + 1} of ${total}` : '');

export const venuePhotoAlt = (venue, { index, total } = {}) => {
  const name = clean(venue?.name);
  const descriptor = [clean(venue?.setting).toLowerCase(), clean(venue?.venueType).toLowerCase(), 'event venue'].filter(Boolean).join(' ');
  const place = [clean(venue?.address?.city), clean(venue?.address?.state)].filter(Boolean).join(', ');

  let alt = `${article(descriptor)} ${descriptor}`;
  alt = name ? `${name}, ${alt}` : capitalise(alt);
  if (place) {
    alt = `${alt} in ${place}`;
  }

  const position = photoPosition(index, total);
  return position ? `${alt}, ${position}` : alt;
};

export default venuePhotoAlt;
